import { Organization } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Issue } from 'validata';
import { useFetchAllCache } from '../../hooks/use-cache.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { formikCheck, issuesToFormik } from '../../utils/validation.js';
import { EditView } from './edit.view.js';
import { ExistingOrganizationPage } from './existing-organization.js';

export const EditOrganizationContent: FC<
  ExistingEntityViewProps<Organization.Type>
> = ({ data: organization, id }) => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const cache = useFetchAllCache('organization');
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik<Organization.Patch>({
    initialValues: {
      allowQuickPatrol: organization.allowQuickPatrol ?? false,
      callSigns: organization.callSigns ?? [],
      eccReplyCcEmail: organization.eccReplyCcEmail,
      emailFrom: organization.emailFrom || Organization.DEFAULTS.emailFrom,
      emailSignOff: organization.emailSignOff,
      hideUnstartedPatrolsBadge:
        organization.hideUnstartedPatrolsBadge ?? false,
      name: organization.name,
      patrollerInstructions: organization.patrollerInstructions,
      patrolMeetUpInstructions: organization.patrolMeetUpInstructions,
      policeStation: organization.policeStation,
      restrictCallSigns: organization.restrictCallSigns ?? false,
    },
    onSubmit: (values) => {
      const url = auth.userInfo.isSuperAdmin
        ? `/api/v1/organization/${encodeURIComponent(id)}`
        : '/api/v1/my/organization';
      return auth.axios
        .patch<{ updated: Organization.Type }>(url, values)
        .then(() => {
          cache.invalidate();
          navigate(-1);
          enqueueSnackbar('Updated', { variant: 'success' });
          return undefined;
        })
        .catch((error: AxiosError<{ issues: Issue[] }>) => {
          console.log(error);
          Sentry.captureException(error);
          if (error.response && error.response.status === 400) {
            if ('issues' in error.response.data) {
              const issues = error.response.data.issues;
              formik.setErrors(issuesToFormik(issues));
              return;
            }
          }
          // TODO setData(error);
        });
    },
    validate: (value) => formikCheck(Organization.patchContract, value),
  });

  return <EditView cancel={() => navigate(-1)} formik={formik} />;
};

export const EditOrganizationPage: FC = () => (
  <ExistingOrganizationPage view={EditOrganizationContent} />
);
